import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AuditEntry, Case } from '../../domain/initial-cases';
import { listCases, getCase, getCaseAudit, getCaseStats, postRecommendation, deleteRecommendation } from '../../api/cases';
import { ApiProblem } from '../../api/client';
import { updateCaseViaApi, type UpdateResult } from '../updateCase';
import { useUIState } from './UIStateContext';

// `updateCase` is intentionally re-created when `cases` changes.
//
// React 18 schedules state-updater functions for the next render, so we read
// the current case directly from the closure-captured `cases` array — the
// updater would otherwise see a snapshot that was set in the same tick.
// `state/updateCase.test.ts` and `AppDataContext.test.tsx` pin this behavior.
//
// PATCHes are additionally SERIALIZED per case (H-1): two updateCase calls in
// the same tick share the closure-captured version, so firing both PATCHes
// concurrently made the server 409 the loser and silently drop its mutation
// (e.g. sendNotification's CURE_WINDOW_OPENED audit row). Each call now
// chains on the previous in-flight PATCH for that case and computes If-Match
// from the array the previous PATCH resolved with.
// `state/actions/lifecycle.test.ts` pins the serialized behavior.
export type UpdateCase = (
  caseId: string,
  updates: Partial<Case> & {
    action?: { code: string; detail?: Record<string, unknown> };
  },
) => void;

// #776 — the analyst recommendation, made and withdrawn through its own
// endpoint (the API takes the recommender from the sign-in). Same per-case
// chain as `updateCase`, because a recommendation bumps the version too: a
// recommend fired in the same tick as a PATCH must take its If-Match from that
// PATCH's result, not from the snapshot both started with.
export type RecommendCase = (caseId: string, recommendation: 'APPROVE' | 'DENY', note?: string) => void;
export type WithdrawRecommendation = (caseId: string) => void;

export interface CaseStats {
  total: number;
  inReview: number;
  successful: number;
  unsuccessful: number;
  pendingNotification: number;
  cureWindowOpen: number;
  appealsActive: number;
  revetsDue: number;
  pendingSync: number;
  synced: number;
  onboardingDraft: number;
  onboardingSubmitted: number;
  activeShaftDisputes: number;
  activeIpDisputes: number;
  // #472 — the three lists on the Intake Queue page. The sidebar badge for
  // that page is their sum; nothing else reads them individually today.
  intakePending: number;
  tnChangeRequestsPending: number;
  companyChangesPending: number;
}

/**
 * Toasts carry a variant because this channel serves two different jobs.
 * `error` is the app's failure channel and persists until dismissed;
 * `success` is a disposable confirmation and clears itself.
 */
export type ToastVariant = 'error' | 'success';

export interface Toast {
  message: string;
  variant: ToastVariant;
}

export interface AppDataValue {
  cases: Case[];
  caseAudit: Record<string, AuditEntry[]>;
  stats: CaseStats;
  now: string;
  updateCase: UpdateCase;
  recommendCase: RecommendCase;
  withdrawRecommendation: WithdrawRecommendation;
  addCase: (c: Case) => void;
  // Local-only state merge — used by the dispute actions to fold an API-
  // returned dispute into a case without going through PATCH /cases (which
  // would silently drop the shaftDisputes / ipDisputes arrays since those
  // aren't case columns). Also refreshes server stats so the Dashboard
  // tiles reflect the new dispute counts.
  mergeLocalCase: (caseId: string, patch: Partial<Case>) => void;
  // Folds a fetched page of server rows into the store: replaces rows the
  // store already has (unless the store's copy has a NEWER version — a
  // confirmed PATCH that landed after the list query ran) and appends rows
  // it doesn't. Used by useCaseQuery so every queue page shares one case
  // store and updateCase / If-Match chaining keep working on paged rows.
  mergeCases: (items: Case[]) => void;
  // #691 — drop a case the server has deleted. addCase's mirror image, and the
  // only removal this store has: every other mutator adds or patches, so
  // without it a deleted registration stayed on screen until the #148 poll's
  // refreshCases happened to drop it from the window. Local-only — the DELETE
  // has already committed by the time this runs.
  removeCase: (caseId: string) => void;
  // #472 — refetch the badge-backing stats now instead of waiting out the #148
  // poll. Pages that action items the sidebar counts call this, so clearing the
  // last pending item clears the badge with it rather than up to 60s later.
  refreshStats: () => void;
  // #474 — the #148 poll's heartbeat, exposed so page-level list queries can
  // refetch on the same cadence instead of each growing its own timer. It
  // increments on every moment #148 already refreshes badge data: an interval
  // tick, a window focus, and the catch-up when a hidden tab becomes visible.
  // Subscribe with usePollTick() (state/usePollTick.ts) rather than reading
  // this directly.
  dataTick: number;
  showToast: (msg: string, variant?: ToastVariant) => void;
  dismissToast: () => void;
  toast: Toast | null;
  casesLoading: boolean;
  casesError: string | null;
}

const EMPTY_STATS: CaseStats = {
  total: 0,
  inReview: 0,
  successful: 0,
  unsuccessful: 0,
  pendingNotification: 0,
  cureWindowOpen: 0,
  appealsActive: 0,
  revetsDue: 0,
  pendingSync: 0,
  synced: 0,
  onboardingDraft: 0,
  onboardingSubmitted: 0,
  activeShaftDisputes: 0,
  activeIpDisputes: 0,
  intakePending: 0,
  tnChangeRequestsPending: 0,
  companyChangesPending: 0,
};

const AppDataContext = createContext<AppDataValue | null>(null);

// #776 — `updateCaseViaApi`'s outcome contract for a write that is not a PATCH:
// the resolved case on success; on 409 the refetched case and the same "another
// user updated" toast; on anything else the problem's DETAIL as the toast (the
// sentence a user can act on — "Withdraw it first…") and the pre-write case.
async function caseWriteViaApi(
  current: Case,
  write: (latest: Case) => Promise<Case>,
  toast: (msg: string) => void,
): Promise<UpdateResult> {
  try {
    return { case: await write(current), outcome: 'updated' };
  } catch (err) {
    if (err instanceof ApiProblem && err.status === 409 && err.extensions.currentVersion !== undefined) {
      const fresh = await getCase(current.id);
      toast('Another user updated this case — showing the latest version.');
      return { case: fresh, outcome: 'conflict' };
    }
    const reason = err instanceof ApiProblem
      ? (err.detail ?? err.title)
      : err instanceof Error ? err.message : String(err);
    toast(`Update failed: ${reason}`);
    return { case: current, outcome: 'error' };
  }
}

// AppDataProvider must sit INSIDE a UIStateProvider — the audit-fetch effect
// keys off `selectedCase?.id` from UI state, and the `updateCase` callback
// pushes into `setSelectedCase` to keep the open detail view in sync.
export function AppDataProvider({ children }: { children: ReactNode }) {
  const { selectedCase, setSelectedCase } = useUIState();

  const [cases, setCases] = useState<Case[]>([]);
  const [casesLoading, setCasesLoading] = useState(true);
  const [casesError, setCasesError] = useState<string | null>(null);
  const [caseAudit, setCaseAudit] = useState<Record<string, AuditEntry[]>>({});
  const [toast, setToast] = useState<Toast | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [stats, setStats] = useState<CaseStats>(EMPTY_STATS);

  // L-6: `now` was recomputed on every render (`new Date().toISOString()`),
  // so it was a fresh string each render and, sitting in the context value's
  // useMemo deps, rebuilt the whole context value every render — defeating the
  // memo — while STILL not ticking between renders (a fresh value only on
  // re-render is not a clock). Hold it in state refreshed on a 60s interval:
  // the reference is stable between ticks (memo holds), and deadlines actually
  // advance once a minute, which is the granularity the DeadlineIndicator
  // "N days remaining" copy needs.
  const [now, setNow] = useState(() => new Date().toISOString());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date().toISOString()), 60_000);
    return () => clearInterval(id);
  }, []);

  // L-8 said "every showToast call site is an error report", so toasts persist
  // until dismissed rather than auto-clearing. That was true when written, but
  // #296/#323 routed confirmations through the same channel — so successes
  // rendered in danger red, announced assertively to screen readers, and sat on
  // screen until dismissed.
  //
  // The L-8 rationale still holds for errors and only for errors: an error that
  // auto-clears is one the operator can miss. A confirmation is the opposite —
  // disposable by definition, and leaving it up makes the NEXT action's result
  // ambiguous ("is that this click's result or the last one's?").
  //
  // The timer is cleared on every call, which is the other half of the L-8 bug:
  // a stale timer kept running when a newer toast replaced the message and
  // cleared the new one early.
  const showToast = useCallback((msg: string, variant: ToastVariant = 'error') => {
    if (toastTimer.current) { clearTimeout(toastTimer.current); toastTimer.current = null; }
    setToast({ message: msg, variant });
    if (variant === 'success') {
      toastTimer.current = setTimeout(() => { setToast(null); toastTimer.current = null; }, 6000);
    }
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimer.current) { clearTimeout(toastTimer.current); toastTimer.current = null; }
    setToast(null);
  }, []);

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // Stats live on the server: a count(*)-based aggregate over the calling
  // role's RLS-scoped view. We treat the server response as source-of-truth
  // and refetch after any mutation (add / update / delete). Computing stats
  // client-side from `cases` would understate the totals because cases is a
  // bounded page of the most recent rows, not the full population.
  const refreshStats = useCallback(() => {
    void getCaseStats().then(setStats).catch(() => { /* keep last known good */ });
  }, []);

  // Refetch the recent-cases window and fold it into the store. Shares the
  // bootstrap effect's merge semantics: window rows lead (recent-first for
  // legacy consumers), a store copy with a NEWER version (a confirmed PATCH
  // that landed after this query started) wins over the fetched row, and rows
  // already in the store but outside the window are appended untouched. This
  // is a read-only refetch — it never disturbs the updateCase PATCH chain.
  const refreshCases = useCallback(() => {
    void listCases(50).then(items => {
      setCases(prev => {
        const prevById = new Map(prev.map(c => [c.id, c]));
        const windowIds = new Set(items.map(c => c.id));
        const windowRows = items.map(c => {
          const existing = prevById.get(c.id);
          return existing && existing.version > c.version ? existing : c;
        });
        return [...windowRows, ...prev.filter(c => !windowIds.has(c.id))];
      });
    }).catch(() => { /* keep last known good */ });
  }, []);

  const addCase = useCallback((c: Case) => {
    setCases(prev => [c, ...prev]);
    refreshStats();
  }, [refreshStats]);

  // #691 — see removeCase on AppDataValue. Clearing the selection matters as
  // much as dropping the row: CaseDetail renders off `selectedCase`, so a
  // delete performed from the case header would otherwise leave the operator
  // looking at a case that no longer exists.
  const removeCase = useCallback((caseId: string) => {
    setCases(prev => prev.filter(c => c.id !== caseId));
    setSelectedCase(prev => (prev && prev.id === caseId ? null : prev));
    refreshStats();
  }, [setSelectedCase, refreshStats]);

  const mergeCases = useCallback((items: Case[]) => {
    if (items.length === 0) return;
    setCases(prev => {
      const incoming = new Map(items.map(c => [c.id, c]));
      const next = prev.map(c => {
        const inc = incoming.get(c.id);
        if (!inc) return c;
        incoming.delete(c.id);
        return inc.version >= c.version ? inc : c;
      });
      return incoming.size > 0 ? [...next, ...incoming.values()] : next;
    });
  }, []);

  const mergeLocalCase = useCallback((caseId: string, patch: Partial<Case>) => {
    setCases(prev => prev.map(c => c.id === caseId ? { ...c, ...patch } : c));
    setSelectedCase(prev => (prev && prev.id === caseId ? { ...prev, ...patch } as Case : prev));
    refreshStats();
  }, [setSelectedCase, refreshStats]);

  useEffect(() => {
    let active = true;
    listCases(50).then(items => {
      if (!active) return;
      // Merge, don't replace: queue-page queries (useCaseQuery) mount before
      // this provider effect runs (child effects fire first), so their rows
      // may already be in the store — a wholesale setCases here would wipe
      // them. Window rows lead so legacy consumers keep recent-first order;
      // anything else already in the store is appended.
      setCases(prev => {
        const prevById = new Map(prev.map(c => [c.id, c]));
        const windowIds = new Set(items.map(c => c.id));
        const windowRows = items.map(c => {
          const existing = prevById.get(c.id);
          return existing && existing.version > c.version ? existing : c;
        });
        return [...windowRows, ...prev.filter(c => !windowIds.has(c.id))];
      });
      setCasesLoading(false);
    }).catch(err => {
      if (!active) return;
      setCasesError(err instanceof Error ? err.message : String(err));
      setCasesLoading(false);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    getCaseStats().then(s => { if (active) setStats(s); }).catch(() => { /* keep zeros */ });
    return () => { active = false; };
  }, []);

  // #474 — see `dataTick` on AppDataValue. One counter, bumped from the single
  // #148 effect below, is what keeps every list on the same heartbeat as the
  // badges; three page-level intervals each re-implementing the hidden-tab
  // teardown is the outcome the issue rules out.
  const [dataTick, setDataTick] = useState(0);

  // #148: badge counts are derived from server data loaded at bootstrap and,
  // before this, refetched only after in-app mutations — so rows created out
  // of band (customer-API intake submissions, other staff users' changes)
  // stayed invisible until a full page reload. Poll the badge-backing data
  // (case stats + the recent-cases window) every 60s and on window focus, but
  // only while the tab is visible: the interval is torn down when the document
  // is hidden and a fresh fetch fires immediately when it becomes visible
  // again, so idle background tabs don't hammer the API. No websockets/SSE in
  // v1 — this reuses the existing fetch paths and is strictly read-only.
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const refreshBadgeData = () => {
      refreshStats();
      refreshCases();
      // Same heartbeat for page-level lists (#474). Bumped here rather than in
      // each branch below so the interval, the focus handler and the
      // becoming-visible catch-up all drive lists exactly when they drive
      // badges — including the hidden-tab pause, which is simply the absence
      // of calls to this function.
      setDataTick(t => t + 1);
    };

    const startPolling = () => {
      if (intervalId !== undefined) return;
      intervalId = setInterval(refreshBadgeData, 60_000);
    };

    const stopPolling = () => {
      if (intervalId === undefined) return;
      clearInterval(intervalId);
      intervalId = undefined;
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        // Becoming visible again may have missed several intervals — catch up
        // once immediately, then resume the cadence.
        refreshBadgeData();
        startPolling();
      }
    };

    // Focus is a strong signal the operator returned to the tab; refetch even
    // if the tab was never hidden (e.g. alt-tab within a visible window).
    const onFocus = () => {
      if (!document.hidden) refreshBadgeData();
    };

    if (!document.hidden) startPolling();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshStats, refreshCases]);

  useEffect(() => {
    if (!selectedCase) return;
    if (caseAudit[selectedCase.id]) return;
    let active = true;
    getCaseAudit(selectedCase.id).then(items => {
      if (!active) return;
      setCaseAudit(prev => ({ ...prev, [selectedCase.id]: items }));
    }).catch(() => {
      // silently fail; audit tab will render empty until next visit
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCase?.id]);

  // Tail of the in-flight PATCH chain per case id. The chain carries the
  // latest server-confirmed Case (not a cases-array snapshot), so each link
  // derives If-Match from the previous PATCH's resolution. Entries are
  // removed once the tail settles, so a later updateCase starts from the
  // fresh closure `cases` rather than a long-stale snapshot.
  const patchChainsRef = useRef(new Map<string, Promise<Case>>());

  // One link on a case's write chain: `run` gets the latest server-confirmed
  // case, so its If-Match is the previous write's result. Shared by the PATCH
  // and the #776 recommendation writes, which bump the same version.
  const chainCaseWrite = useCallback((
    caseId: string,
    known: Case,
    run: (latest: Case) => Promise<UpdateResult>,
  ) => {
    const chains = patchChainsRef.current;
    const tail = chains.get(caseId) ?? Promise.resolve(known);
    const next = tail.then(latest =>
      run(latest).then(({ case: resolved, outcome }) => {
        // M-5: merge ONLY this case via a functional update — concurrent
        // addCase / mergeLocalCase / list-fetch merges that landed while the
        // PATCH was in flight survive resolution.
        setCases(prev => prev.map(c => (c.id === caseId ? resolved : c)));
        setSelectedCase(prev => (prev && prev.id === caseId ? resolved : prev));
        if (outcome !== 'error') {
          // The server emitted a new audit row for this PATCH; refresh the
          // cached enriched history so the audit tab reflects it.
          getCaseAudit(caseId).then(items => {
            setCaseAudit(prev => ({ ...prev, [caseId]: items }));
          }).catch(() => { /* silent — next case-open will retry */ });
          // Status / decision / dispute / onboarding changes can shift any of
          // the stat tiles, so re-pull the authoritative aggregate.
          refreshStats();
        }
        return resolved;
      }),
    );
    chains.set(caseId, next);
    void next.finally(() => {
      if (chains.get(caseId) === next) chains.delete(caseId);
    });
  }, [setSelectedCase, refreshStats]);

  const updateCase = useCallback<UpdateCase>((caseId, updates) => {
    const known = cases.find(c => c.id === caseId);
    if (!known) return;
    const { action: _action, ...patch } = updates;
    setCases(prev => prev.map(c => c.id === caseId ? { ...c, ...patch } : c));
    setSelectedCase(prev => (prev && prev.id === caseId ? { ...prev, ...patch } as Case : prev));
    chainCaseWrite(caseId, known, latest => updateCaseViaApi(latest, updates, { toast: showToast }));
  }, [cases, showToast, setSelectedCase, chainCaseWrite]);

  // No optimistic merge, unlike updateCase: who recommended is decided by the
  // server from the sign-in, so there is nothing true to show until it answers.
  const recommendCase = useCallback<RecommendCase>((caseId, recommendation, note) => {
    const known = cases.find(c => c.id === caseId);
    if (!known) return;
    chainCaseWrite(caseId, known, latest => caseWriteViaApi(
      latest, l => postRecommendation(l.id, l.version, recommendation, note), showToast,
    ));
  }, [cases, showToast, chainCaseWrite]);

  const withdrawRecommendation = useCallback<WithdrawRecommendation>((caseId) => {
    const known = cases.find(c => c.id === caseId);
    if (!known) return;
    chainCaseWrite(caseId, known, latest => caseWriteViaApi(
      latest, l => deleteRecommendation(l.id, l.version), showToast,
    ));
  }, [cases, showToast, chainCaseWrite]);

  const value: AppDataValue = useMemo(() => ({
    cases, caseAudit, stats, now,
    updateCase, recommendCase, withdrawRecommendation,
    addCase, mergeLocalCase, mergeCases, removeCase, refreshStats, dataTick, showToast, dismissToast,
    toast, casesLoading, casesError,
  }), [cases, caseAudit, stats, now, updateCase, recommendCase, withdrawRecommendation, addCase, mergeLocalCase, mergeCases, removeCase, refreshStats, dataTick, showToast, dismissToast, toast, casesLoading, casesError]);

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData(): AppDataValue {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within an AppDataProvider');
  return ctx;
}

export function useCases(): Case[] {
  return useAppData().cases;
}

export function useCase(id: string | null | undefined): Case | undefined {
  const cases = useCases();
  return id ? cases.find(c => c.id === id) : undefined;
}

export function useUpdateCase(): UpdateCase {
  return useAppData().updateCase;
}

export function useCaseStats(): CaseStats {
  return useAppData().stats;
}

export function useNow(): string {
  return useAppData().now;
}

// useSelectedCase resolves UI's selectedCase against the live cases array, so
// sub-tabs and modals always render against the freshest state. Mirrors the
// inline `cases.find(x => x.id === selectedCase.id) ?? selectedCase` pattern
// that renderCaseDetail used before extraction (App.tsx pre-PR-D line 1307).
// If the case has been deleted from cases since selection, falls back to the
// last-known selectedCase rather than crashing.
export function useSelectedCase(): import('../../domain/initial-cases').Case | null {
  const cases = useCases();
  // Lazy-import to avoid a circular dep at module evaluation.
  const ui = useUIState();
  if (!ui.selectedCase) return null;
  return cases.find(c => c.id === ui.selectedCase!.id) ?? ui.selectedCase;
}
